import { fieldsOf, listOf, pathTo, report, whole, type Place } from "./checks.ts";
import { BASIS_POINTS, HIT_CLASSES, MAX_EFFECT_TICKS, type Ability, type Effect, type HitLanding, type OnHit } from "./types.ts";

/**
 * An ability's chances on a hit (ADR-079's amendment of 2026-10-02, item 3): the record check of `onHit`, each an effect
 * the effects table holds and the ability's `effects` does not land already, at most once, a chance from 1 to 10,000
 * basis points and, for each hit class, the stacks it lands and a range of ticks; and the check across the tables that
 * each class's stacks fit the effect's `maxStacks` and its ticks its `durationTicks` and a periodic effect's interval.
 */

const ON_HIT_FIELDS = ["effect", "chanceBasisPoints", ...HIT_CLASSES] as const;
const LANDING_FIELDS = ["stacks", "minTicks", "maxTicks"] as const;
const MAX_STACKS = 255;

/**
 * Checks how one hit class lands an effect: whole stacks from 1 to 255, and ticks from 1 to a `u16`, never running
 * backwards.
 *
 * @param place Where to report.
 * @param value The landing.
 * @param path Its path.
 * @returns The landing, or null.
 */
function landingOf(place: Place, value: unknown, path: string): HitLanding | null {
  const fields = fieldsOf(place, value, path, LANDING_FIELDS, "a hit class's landing");
  if (fields === null) {
    return null;
  }
  const stacks = whole(place, fields["stacks"], `${path}.stacks`, 1, MAX_STACKS);
  const minTicks = whole(place, fields["minTicks"], `${path}.minTicks`, 1, MAX_EFFECT_TICKS);
  const maxTicks = whole(place, fields["maxTicks"], `${path}.maxTicks`, 1, MAX_EFFECT_TICKS);
  if (stacks === null || minTicks === null || maxTicks === null || Object.keys(fields).length !== LANDING_FIELDS.length) {
    return null;
  }
  if (minTicks > maxTicks) {
    report(place, `${path} runs from ${minTicks} to ${maxTicks} ticks`);
    return null;
  }
  return { stacks, minTicks, maxTicks };
}

/**
 * Checks one entry of an ability's `onHit`.
 *
 * @param place Where to report.
 * @param value The entry.
 * @param path Its path.
 * @param effectIds The ids the effects table holds.
 * @param applied The ids the ability's `effects` lands on every hit.
 * @returns The entry, or null.
 */
function entryOf(place: Place, value: unknown, path: string, effectIds: ReadonlySet<string>, applied: readonly string[]): OnHit | null {
  const fields = fieldsOf(place, value, path, ON_HIT_FIELDS, "a chance on a hit");
  if (fields === null) {
    return null;
  }
  const effect = fields["effect"];
  let ok = Object.keys(fields).length === ON_HIT_FIELDS.length;
  if (typeof effect !== "string" || !effectIds.has(effect)) {
    report(place, `onHit lands the effect ${String(effect)}, which the effects table does not hold`);
    ok = false;
  } else if (applied.includes(effect)) {
    report(place, `onHit names ${effect}, which its effects already land on every hit`);
    ok = false;
  }
  const chanceBasisPoints = whole(place, fields["chanceBasisPoints"], `${path}.chanceBasisPoints`, 1, BASIS_POINTS);
  const light = landingOf(place, fields["light"], `${path}.light`);
  const normal = landingOf(place, fields["normal"], `${path}.normal`);
  const critical = landingOf(place, fields["critical"], `${path}.critical`);
  if (!ok || chanceBasisPoints === null || light === null || normal === null || critical === null) {
    return null;
  }
  return { effect: effect as string, chanceBasisPoints, light, normal, critical };
}

/**
 * Checks an ability's `onHit`: a list of entries, no effect named twice.
 *
 * @param place Where to report.
 * @param value The list.
 * @param effectIds The ids the effects table holds.
 * @param applied The ids the ability's `effects` lands on every hit, or null when they were wrong.
 * @returns The entries, or null.
 */
export function onHitOf(place: Place, value: unknown, effectIds: ReadonlySet<string>, applied: readonly string[] | null): OnHit[] | null {
  const list = listOf(place, value, "onHit");
  if (list === null) {
    return null;
  }
  let ok = true;
  const entries: OnHit[] = [];
  for (const [index, item] of list.entries()) {
    const entry = entryOf(place, item, pathTo("onHit", index, true), effectIds, applied ?? []);
    if (entry === null) {
      ok = false;
    } else if (entries.some((known) => known.effect === entry.effect)) {
      report(place, `onHit names ${entry.effect} twice`);
      ok = false;
    } else {
      entries.push(entry);
    }
  }
  return ok ? entries : null;
}

/**
 * Checks each ability's chances on a hit against the effects they land: each class's stacks at most the effect's
 * `maxStacks`, its `maxTicks` at most the effect's `durationTicks`, and, for a periodic effect, both ends a whole number
 * of its interval, so a landing ticks a whole number of times.
 *
 * @param abilities The abilities that passed their checks, with the names their problems carry.
 * @param effects The effects that passed theirs.
 * @param problems Where problems go.
 */
export function checkOnHit(abilities: readonly { readonly name: string; readonly record: Ability }[], effects: readonly { readonly record: Effect }[], problems: Place["problems"]): void {
  const byId = new Map(effects.map(({ record }) => [record.id, record]));
  for (const { name, record } of abilities) {
    const place: Place = { table: "abilities", id: name, problems };
    for (const [index, entry] of record.onHit.entries()) {
      const effect = byId.get(entry.effect);
      if (effect === undefined) {
        continue;
      }
      for (const hitClass of HIT_CLASSES) {
        const landing = entry[hitClass];
        const path = `${pathTo("onHit", index, true)}.${hitClass}`;
        if (landing.stacks > effect.maxStacks) {
          report(place, `${path}.stacks is ${landing.stacks}, and ${effect.id} holds at most ${effect.maxStacks}`);
        }
        if (landing.maxTicks > effect.durationTicks) {
          report(place, `${path}.maxTicks is ${landing.maxTicks}, and ${effect.id} lasts at most ${effect.durationTicks}`);
        }
        const every = effect.periodic?.everyTicks ?? 1;
        for (const end of ["minTicks", "maxTicks"] as const) {
          if (landing[end] % every !== 0) {
            report(place, `${path}.${end} is ${landing[end]}, and ${effect.id} ticks every ${every}`);
          }
        }
      }
    }
  }
}
